// OrderSheetModal.jsx — the shared "order sheet" popup (Brian, 2026-08-24):
// a packing-list view of an order with a list ↔ photo-grid toggle. Used by
// the Cart (current order, pre-submit) and My Orders (any past order), so
// customers can always look back at exactly what they ordered — SKU, name,
// quantity — and flip to pictures for a visual check. No prices, no notes.
import { useState } from "react";
import { useLang } from "../i18n/LanguageContext";

// rows: [{ key, sku, name, qty, imageUrl, isReturn, creditLine, creditDesc, freeGoods }]
// originalRows (optional): same shape but in the ORDER THE LINES WERE WRITTEN
// (cart entry / paste order) — for checking against a handwritten PO
// (Brian, 2026-08-26). When provided, a second toggle switches sorting.
export default function OrderSheetModal({ title, customerName, dateLabel, rows, originalRows = null, onClose }) {
  const { t } = useLang();
  const [view, setView] = useState("list");
  const [sortMode, setSortMode] = useState("packing"); // packing | original
  const shown = sortMode === "original" && originalRows ? originalRows : rows;

  function rowLabel(r) {
    return (
      <>
        {r.isReturn && <span className="return-tag">↩ </span>}
        {r.creditLine ? <>Credit — {r.creditDesc}</> : r.name}
        {r.freeGoods ? " 🎁" : ""}
      </>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content order-sheet-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <div className="packing-header">
          <h1>{title}</h1>
          {customerName && <p className="packing-customer"><strong>{customerName}</strong></p>}
          {dateLabel && <p className="packing-meta">{dateLabel}</p>}
          <button
            className="mini-btn sheet-view-toggle"
            onClick={() => setView(v => v === "list" ? "photos" : "list")}
          >
            {view === "list" ? t("viewPhotos") : t("viewList")}
          </button>
          {originalRows && (
            <button
              className="mini-btn sheet-view-toggle"
              onClick={() => setSortMode(m => m === "packing" ? "original" : "packing")}
            >
              {sortMode === "packing" ? t("sortOriginal") : t("sortPacking")}
            </button>
          )}
        </div>
        {view === "photos" ? (
          <div className="order-sheet-photo-grid">
            {shown.map(r => (
              <div key={r.key} className="os-photo-card">
                {r.creditLine ? (
                  <div className="os-photo-none">💲</div>
                ) : r.imageUrl ? (
                  <img src={r.imageUrl} alt={r.name} loading="lazy" />
                ) : (
                  <div className="os-photo-none">{t("noPhoto")}</div>
                )}
                <p className="os-photo-name">{rowLabel(r)}</p>
                <span className="os-photo-qty">×{r.qty}</span>
              </div>
            ))}
          </div>
        ) : (
          <table className="packing-table">
            <thead>
              <tr><th>{t("sku")}</th><th>{t("product")}</th><th className="qty-col">{t("qty")}</th></tr>
            </thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.key}>
                  <td>{r.sku}</td>
                  <td>{rowLabel(r)}</td>
                  <td className="qty-col">{r.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
